import {useMemo} from 'react'

import {filterConversationComments, filterThreads} from './utils/filter-items'

import {usePullRequestBasePath, useThreads} from '../../stores/layout-selectors'
import {getConversationCommentsAuthors, getThreadsAuthors, type AuthorFilterItem} from './utils/get-author-filters'
import type {CommentsFilterState} from './use-comments-filter-state'
import {useInfiniteConversationCommentsQuery} from '../../page-data/loaders/use-conversation-comments'
import type {CommentsDisplayMode} from './utils/display-mode'
import {isBotOrAgentCommentAuthor} from '../../page-data/payloads/comment-interfaces'

export interface UseAuthorFilterItemsOptions {
  filterState: CommentsFilterState
  displayMode: CommentsDisplayMode
}

/** Returns the list of all authors in the conversation with filters applied, excluding any applied author filters. */
export function useAuthorFilterItems({filterState, displayMode}: UseAuthorFilterItemsOptions): AuthorFilterItem[] {
  const threadPreviews = useThreads()

  const basePath = usePullRequestBasePath()

  const conversationComments = useInfiniteConversationCommentsQuery({
    basePath,
    enabled: displayMode === 'conversation',
  }).data?.comments

  const authorList = useMemo(() => {
    // Author counts reflect every other active filter, but not the author filter itself -- otherwise
    // selecting an author would zero out the counts of all the others
    const authorCountFilterState = {
      ...filterState,
      selectedAuthors: undefined,
    }

    const allAuthors =
      displayMode === 'conversation'
        ? getConversationCommentsAuthors(
            conversationComments ?? [],
            filterConversationComments(conversationComments ?? [], authorCountFilterState),
          )
        : getThreadsAuthors(threadPreviews, filterThreads(threadPreviews, authorCountFilterState))

    return filterState.showAgents === false
      ? allAuthors.filter(author => !isBotOrAgentCommentAuthor(author))
      : allAuthors
  }, [filterState, displayMode, conversationComments, threadPreviews])

  return authorList
}
